/**
 * LeadQualify — /#/leads/:id/qualify — Step 3 of the Lead-to-AE wizard.
 *
 * P0-5 — Full BDR step panel:
 *   - Outreach attempts log (date, channel, outcome) persisted to leads_activity
 *   - BDR notes textarea
 *   - M-PACT qualification checklist (Methodology, Process / Prioritization, Accountability, Cadence / Coaching, Timing / Timeline management)
 *   - Explicit "BDR → SQL Handoff" button gated on M-PACT qualification complete
 *   - Shows BDR's name/avatar from session (not literal string "current_user")
 *
 * Disposition gate: only Qualified leads proceed to AE assignment.
 */

import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/session";
import { RibbonProgress } from "./RibbonProgress";

type Answer = "yes" | "no" | "unsure" | "";
type Disposition = "qualified" | "disqualified" | "nurture" | "invalid" | "";

// M-PACT criteria — Bryan's locked proprietary qualification methodology
// M — Methodology | P — Process / Prioritization | A — Accountability | C — Cadence / Coaching | T — Timing / Timeline management
const MPACT_CRITERIA: { id: string; label: string; title: string; help: string }[] = [
  {
    id: "m_methodology",
    title: "Methodology",
    label: "The buyer has a defined buying methodology / decision framework we can map to",
    help: "Do they follow a structured evaluation process? Can we map our approach to how they buy?",
  },
  {
    id: "p_process",
    title: "Process / Prioritization",
    label: "The buyer's purchase process is clear, documented, with named steps and owners",
    help: "We know the steps, who owns each one, and what approvals are required to move forward.",
  },
  {
    id: "a_accountability",
    title: "Accountability",
    label: "We know who on the buyer side is accountable for the outcome and signs off",
    help: "A named individual is accountable for the business result — not just 'the committee.'",
  },
  {
    id: "c_coaching",
    title: "Cadence / Coaching",
    label: "We have a coach inside the account meeting on a regular cadence",
    help: "A champion or internal advocate is meeting with us regularly and sharing insider context.",
  },
  {
    id: "t_timeline",
    title: "Timing / Timeline management",
    label: "There is a real timeline tied to a business event creating urgency",
    help: "A named business event (fiscal deadline, regulatory date, executive mandate) drives a real decision window.",
  },
];

const CHANNELS = [
  { value: "call",     label: "Phone call" },
  { value: "email",    label: "Email" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "text",     label: "Text / SMS" },
  { value: "other",    label: "Other" },
];

const OUTCOMES = [
  "Left voicemail",
  "No answer",
  "Connected — not interested",
  "Connected — interested, follow-up scheduled",
  "Connected — meeting booked",
  "Email sent — no reply",
  "Email replied — positive",
  "Email replied — negative",
  "LinkedIn connected",
  "LinkedIn message sent",
  "Other",
];

const BTN_PILL =
  "flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-all duration-150 text-center cursor-pointer";

function MpactPills({
  value,
  onChange,
}: {
  value: Answer;
  onChange: (v: Answer) => void;
}) {
  const options: { v: Answer; label: string }[] = [
    { v: "yes",    label: "Yes" },
    { v: "unsure", label: "Not sure" },
    { v: "no",     label: "No" },
  ];
  return (
    <div className="flex gap-2">
      {options.map(({ v, label }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(value === v ? "" : v)}
          className={`${BTN_PILL} ${
            value === v
              ? v === "yes"
                ? "border-[#0B0B0F] bg-[#0B0B0F] text-white"
                : v === "no"
                ? "border-[#DC2626] bg-[#DC2626] text-white"
                : "border-neutral-600 bg-neutral-700 text-white"
              : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400 hover:bg-neutral-50"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

type OutreachAttempt = {
  id: string;
  date: string;
  channel: string;
  outcome: string;
  notes?: string;
  persisted?: boolean;
};

export default function LeadQualify() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const prospectId = params.id;

  // Prospect + session
  const [prospect, setProspect] = useState<any>(null);
  const [sessionUser, setSessionUser] = useState<{ name: string; email: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);

  // M-PACT answers
  const [answers, setAnswers] = useState<Record<string, Answer>>({});

  // BDR notes
  const [bdrNotes, setBdrNotes] = useState("");

  // Disposition
  const [disposition, setDisposition] = useState<Disposition>("");

  // Outreach log
  const [outreachLog, setOutreachLog] = useState<OutreachAttempt[]>([]);
  const [showAddOutreach, setShowAddOutreach] = useState(false);
  const [newChannel, setNewChannel] = useState("call");
  const [newOutcome, setNewOutcome] = useState(OUTCOMES[0]);
  const [newOutcomeCustom, setNewOutcomeCustom] = useState("");
  const [newAttemptNotes, setNewAttemptNotes] = useState("");
  const [loggingAttempt, setLoggingAttempt] = useState(false);

  // Saving state
  const [saving, setSaving] = useState(false);

  // M-PACT score
  const mpactYesCount = MPACT_CRITERIA.filter((q) => answers[q.id] === "yes").length;
  const mpactGate = mpactYesCount >= 3; // require 3+ YES for handoff

  useEffect(() => {
    if (!prospectId) return;
    let cancelled = false;

    async function load() {
      try {
        // Load prospect and session in parallel
        const [p, session, activity] = await Promise.all([
          api<any>(`/api/prospects/${prospectId}`),
          api<any>("/api/me").catch(() => null),
          api<OutreachAttempt[]>(`/api/prospects/${prospectId}/bdr-activity`).catch(() => []),
        ]);
        if (cancelled) return;
        setProspect(p);
        if (session) {
          setSessionUser({ name: session.name ?? session.email ?? "BDR", email: session.email ?? "", role: session.role ?? "bdr" });
        }
        // Restore prior disposition / notes
        if (p?.bdr_disposition) setDisposition(p.bdr_disposition as Disposition);
        if (p?.bdr_notes) setBdrNotes(p.bdr_notes);
        // Map persisted activity rows to local log
        if (activity && activity.length > 0) {
          setOutreachLog(
            activity.map((a: any) => ({
              id: String(a.id),
              date: a.created_at ? new Date(a.created_at).toLocaleDateString() : new Date().toLocaleDateString(),
              channel: a.channel,
              outcome: a.outcome,
              notes: a.notes,
              persisted: true,
            }))
          );
        }
      } catch {
        /* cosmetic — continue */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [prospectId]);

  // Log outreach attempt to leads_activity
  async function handleLogAttempt() {
    if (loggingAttempt) return;
    const outcomeStr = newOutcome === "Other" ? (newOutcomeCustom.trim() || "Other") : newOutcome;
    setLoggingAttempt(true);
    try {
      await api(`/api/prospects/${prospectId}/bdr-activity`, {
        method: "POST",
        body: JSON.stringify({
          channel: newChannel,
          outcome: outcomeStr,
          notes: newAttemptNotes.trim(),
          mpact_score: mpactYesCount,
        }),
      });
      const newEntry: OutreachAttempt = {
        id: `local-${Date.now()}`,
        date: new Date().toLocaleDateString(),
        channel: newChannel,
        outcome: outcomeStr,
        notes: newAttemptNotes.trim(),
        persisted: true,
      };
      setOutreachLog((prev) => [...prev, newEntry]);
      setNewChannel("call");
      setNewOutcome(OUTCOMES[0]);
      setNewOutcomeCustom("");
      setNewAttemptNotes("");
      setShowAddOutreach(false);
      toast({ title: "Outreach attempt logged" });
    } catch (e: any) {
      toast({
        title: "Could not log attempt",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoggingAttempt(false);
    }
  }

  function buildBdrNeedStr(): string {
    const yesLabels = MPACT_CRITERIA.filter((q) => answers[q.id] === "yes")
      .map((q) => q.title)
      .join(", ");
    const parts: string[] = [];
    if (yesLabels) parts.push(`M-PACT confirmed: ${yesLabels}`);
    if (bdrNotes.trim()) parts.push(bdrNotes.trim());
    return parts.join(" — ") || "BDR qualification completed.";
  }

  async function persistDisposition(d: Disposition): Promise<boolean> {
    if (!d) return false;
    try {
      await api(`/api/prospects/${prospectId}/bdr-disposition`, {
        method: "POST",
        body: JSON.stringify({ disposition: d, bdr_notes: buildBdrNeedStr() }),
      });
      return true;
    } catch (e: any) {
      toast({
        title: "Could not save disposition",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }

  async function handleSqlHandoff() {
    if (saving) return;
    if (!mpactGate) {
      toast({
        title: "Checkpoint not met",
        description: `Confirm at least 3 of 5 M-PACT criteria (currently ${mpactYesCount}/5) before handing off to AE.`,
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    const ok = await persistDisposition("qualified");
    setSaving(false);
    if (!ok) return;
    const bantNeed = encodeURIComponent(buildBdrNeedStr());
    navigate(`/leads/${prospectId}/assign?bant=${bantNeed}`);
  }

  async function handleStopHere(d: Exclude<Disposition, "qualified" | "">) {
    if (saving) return;
    setSaving(true);
    const ok = await persistDisposition(d);
    setSaving(false);
    if (!ok) return;
    navigate(`/leads/${prospectId}/success?status=${d}`);
  }

  const CHANNEL_LABEL: Record<string, string> = {
    call:     "Call",
    email:    "Email",
    linkedin: "LinkedIn",
    text:     "Text",
    other:    "Other",
  };

  const dispositionPillBase =
    "flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-all duration-150 text-center cursor-pointer";

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/leads/${prospectId}/route-bdr`)}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
              aria-label="Back"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M11 13.5L6.5 9 11 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 leading-none mb-0.5">Mi6 Attilius</div>
              <div className="text-sm font-bold text-[#0B0B0F] leading-none">
                BDR Qualify
                {prospect && <span className="ml-1.5 font-normal text-neutral-500">— {prospect.company}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* BDR avatar — shows real session user */}
            {sessionUser && (
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                  style={{ background: "#0B0B0F" }}
                  title={sessionUser.name}
                >
                  {sessionUser.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-[11px] text-neutral-600 hidden sm:block">{sessionUser.name}</span>
              </div>
            )}
            <div className="text-[10px] text-neutral-500">Step 3 of 5</div>
          </div>
        </div>
        <RibbonProgress step={3} />
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-7">
          <h1 className="text-[22px] font-bold text-[#0B0B0F] tracking-tight">BDR outreach &amp; qualification</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Log outreach attempts, run M-PACT discovery, then hand off to AE when ready.
          </p>
        </div>

        {/* ── 1. Outreach Attempts Log ─────────────────────────────────────── */}
        <section className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">
              Outreach attempts ({outreachLog.length})
            </h2>
            {!showAddOutreach && (
              <button
                type="button"
                onClick={() => setShowAddOutreach(true)}
                className="flex items-center gap-1 text-xs font-semibold text-[#0B0B0F] hover:opacity-70 transition-opacity"
                data-testid="log-attempt-toggle"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
                Log attempt
              </button>
            )}
          </div>

          {/* Existing attempts */}
          {outreachLog.length > 0 && (
            <div className="space-y-2 mb-3">
              {outreachLog.map((a) => (
                <div
                  key={a.id}
                  className="bg-white rounded-xl border border-neutral-200 px-4 py-3 flex items-start gap-3"
                  data-testid="outreach-row"
                >
                  <div className="shrink-0 mt-0.5 text-neutral-400 text-[11px] tabular-nums w-20">{a.date}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700">
                        {CHANNEL_LABEL[a.channel] ?? a.channel}
                      </span>
                      <span className="text-sm text-neutral-700">{a.outcome}</span>
                    </div>
                    {a.notes && (
                      <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">{a.notes}</p>
                    )}
                  </div>
                  {a.persisted && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0 mt-1 text-emerald-500" aria-label="Saved">
                      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M4.5 7l2 2 3-3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}

          {outreachLog.length === 0 && !showAddOutreach && (
            <div className="bg-white rounded-xl border border-dashed border-neutral-300 px-4 py-6 text-center">
              <p className="text-sm text-neutral-500">No outreach attempts yet. Log your first contact.</p>
            </div>
          )}

          {/* Add outreach form */}
          {showAddOutreach && (
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4 animate-in fade-in duration-150">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1">
                New outreach attempt
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5">
                    Channel
                  </label>
                  <select
                    className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-800 focus:border-[#0B0B0F] outline-none cursor-pointer"
                    value={newChannel}
                    onChange={(e) => setNewChannel(e.target.value)}
                    data-testid="attempt-channel"
                  >
                    {CHANNELS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5">
                    Outcome
                  </label>
                  <select
                    className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-800 focus:border-[#0B0B0F] outline-none cursor-pointer"
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(e.target.value)}
                    data-testid="attempt-outcome"
                  >
                    {OUTCOMES.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              {newOutcome === "Other" && (
                <input
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-800 focus:border-[#0B0B0F] outline-none"
                  placeholder="Describe outcome..."
                  value={newOutcomeCustom}
                  onChange={(e) => setNewOutcomeCustom(e.target.value)}
                  data-testid="attempt-outcome-custom"
                />
              )}

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5">
                  Notes (optional)
                </label>
                <textarea
                  className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-800 placeholder-neutral-400 focus:border-[#0B0B0F] outline-none resize-none"
                  rows={2}
                  value={newAttemptNotes}
                  onChange={(e) => setNewAttemptNotes(e.target.value)}
                  placeholder="Key detail from this touchpoint..."
                  data-testid="attempt-notes"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleLogAttempt}
                  disabled={loggingAttempt}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#0B0B0F] text-white text-sm font-semibold hover:bg-[#374151] disabled:opacity-40 transition-all"
                  data-testid="log-attempt-save"
                >
                  {loggingAttempt ? "Saving…" : "Save attempt"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddOutreach(false)}
                  className="px-4 py-2.5 rounded-lg border border-neutral-200 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── 2. M-PACT Qualification Checklist ────────────────────────────── */}
        <section className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">
              M-PACT Qualification
            </h2>
            {mpactYesCount > 0 && (
              <div
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold"
                style={{
                  background: mpactGate ? "#0B0B0F0D" : "#6B6B7012",
                  color: mpactGate ? "#0B0B0F" : "#6B6B70",
                  border: `1px solid ${mpactGate ? "#0B0B0F30" : "#E5E5E7"}`,
                }}
              >
                <span className="tabular-nums">{mpactYesCount}/5</span>
                {mpactGate ? " — ready for handoff" : " — needs more signals"}
              </div>
            )}
          </div>

          <div className="space-y-3">
            {MPACT_CRITERIA.map((q) => (
              <div
                key={q.id}
                className="bg-white rounded-xl border border-neutral-200 p-5 shadow-sm transition-all duration-150"
                data-testid={`mpact-${q.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white"
                    style={{ background: answers[q.id] === "yes" ? "#0B0B0F" : answers[q.id] === "no" ? "#DC2626" : "#D1D5DB" }}
                  >
                    {q.title.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-400 mb-0.5">
                      {q.title}
                    </div>
                    <p className="text-sm font-semibold text-[#0B0B0F] leading-snug">{q.label}</p>
                    <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">{q.help}</p>
                  </div>
                </div>
                <MpactPills
                  value={answers[q.id] ?? ""}
                  onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
                />
              </div>
            ))}
          </div>
        </section>

        {/* ── 3. BDR Notes ─────────────────────────────────────────────────── */}
        <section className="mb-5">
          <div className="bg-white rounded-xl border border-neutral-200 p-5 shadow-sm">
            <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5">
              BDR notes — discovery summary
            </label>
            <textarea
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-800 placeholder-neutral-400 focus:border-[#0B0B0F] focus:ring-2 focus:ring-neutral-200/60 outline-none transition-all resize-none"
              rows={3}
              value={bdrNotes}
              onChange={(e) => setBdrNotes(e.target.value)}
              placeholder="Summarise the M-PACT signals: methodology fit, process clarity, accountability, coaching access, and timeline urgency discovered during outreach..."
              data-testid="bdr-notes"
            />
            {sessionUser && (
              <div className="flex items-center gap-2 mt-2">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                  style={{ background: "#0B0B0F" }}
                >
                  {sessionUser.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-[11px] text-neutral-500">
                  Notes by <strong className="text-neutral-700">{sessionUser.name}</strong>
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ── 4. Disposition ───────────────────────────────────────────────── */}
        <section className="mb-5">
          <div className="bg-white rounded-xl border border-neutral-200 p-5 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-2.5">
              Disposition
            </div>
            <div className="grid grid-cols-2 gap-2">
              {([
                { v: "qualified",    label: "Qualified",    color: "border-[#0B0B0F] bg-[#0B0B0F] text-white" },
                { v: "nurture",      label: "Nurture",      color: "border-orange-600 bg-orange-600 text-white" },
                { v: "disqualified", label: "Disqualified", color: "border-[#DC2626] bg-[#DC2626] text-white" },
                { v: "invalid",      label: "Invalid",      color: "border-neutral-700 bg-neutral-700 text-white" },
              ] as { v: Disposition; label: string; color: string }[]).map((d) => (
                <button
                  key={d.v}
                  type="button"
                  onClick={() => setDisposition(disposition === d.v ? "" : d.v)}
                  className={`${dispositionPillBase} ${
                    disposition === d.v
                      ? d.color
                      : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400 hover:bg-neutral-50"
                  }`}
                  data-testid={`disposition-${d.v}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-neutral-500 mt-2.5 leading-relaxed">
              Only <strong>Qualified</strong> leads proceed to AE assignment.
            </p>
          </div>
        </section>

        {/* ── 5. SQL Handoff Gate ──────────────────────────────────────────── */}
        {!mpactGate && (
          <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-orange-50 border border-orange-200">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" aria-hidden>
              <circle cx="8" cy="8" r="7" stroke="#CA8A04" strokeWidth="1.3" />
              <path d="M8 4.5v4" stroke="#CA8A04" strokeWidth="1.3" strokeLinecap="round" />
              <circle cx="8" cy="11.5" r="0.6" fill="#CA8A04" />
            </svg>
            <p className="text-[12px] text-yellow-800 leading-relaxed">
              <strong>Checkpoint:</strong> confirm at least 3 of 5 M-PACT criteria (currently{" "}
              <span className="font-bold">{mpactYesCount}/5</span>) to unlock the SQL handoff button.
            </p>
          </div>
        )}

        {/* Primary CTA: BDR → SQL Handoff */}
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleSqlHandoff}
            disabled={!mpactGate || disposition === "" || saving}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0B0B0F] text-white text-sm font-bold tracking-wide hover:bg-[#374151] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-sm"
            data-testid="sql-handoff-btn"
          >
            {saving ? (
              "Saving…"
            ) : (
              <>
                BDR → SQL Handoff
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>

          {disposition && disposition !== "qualified" && (
            <button
              type="button"
              onClick={() => handleStopHere(disposition as Exclude<Disposition, "qualified" | "">)}
              disabled={saving}
              className="px-5 py-3 rounded-xl border border-[#DC2626] text-sm font-semibold text-[#DC2626] hover:bg-red-50 transition-colors"
              data-testid="close-bdr-loop"
            >
              {saving ? "Saving…" : `Close as ${disposition}`}
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate("/dashboards/bdr")}
            className="px-4 py-3 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-500 hover:bg-neutral-50 transition-colors"
          >
            Save and exit
          </button>
        </div>

        <p className="text-[11px] text-neutral-500 mt-3 px-1 leading-relaxed">
          <strong className="text-neutral-700">SQL handoff</strong> is gated on M-PACT ({mpactYesCount}/5
          confirmed) and a <em>Qualified</em> disposition. The BDR step cannot be skipped from this
          screen.
        </p>
      </main>
    </div>
  );
}
